import { useCollectionView } from '../../app/use-collection-view.js';
import { useIsWideViewport, type CivilDate, IconButton } from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CalendarClock, ClipboardCheck, CalendarDays } from 'lucide-react';
import {
  getApiError,
  schedulingApi,
  type Appointment,
  type AppointmentStatusFilter,
  type AppointmentTiming,
} from '../../lib/api.js';
import { formatDate, formatHourMinute } from '../../lib/time-zone.js';
import { useNow } from '../../lib/use-now.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
} from '../../app/data-view.js';
import type { ClientAppointmentsFilter, ClientPageView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { roomLabel, professionalLabel, civilMoment } from './calendar-model.js';
import { isLive } from './confirmation.js';
import { AppointmentStatusPill } from './shared.js';

const filterOptions: Array<{ value: ClientAppointmentsFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'UPCOMING', label: 'Próximos' },
  { value: 'TO_CLOSE', label: 'Abertos' },
  { value: 'COMPLETED', label: 'Realizados' },
  { value: 'CANCELLED', label: 'Cancelados' },
  { value: 'NO_SHOW', label: 'Faltas' },
];

/// Cada filtro da página do cliente em termos da agenda: próximos começam daqui para a frente; a
/// fechar já começaram e seguem agendados ou confirmados. Próximos vêm do mais perto ao mais longe;
/// o resto, do mais recente para trás.
const queryOf: Record<
  ClientAppointmentsFilter,
  { status: AppointmentStatusFilter; timing: AppointmentTiming; direction: 'asc' | 'desc' }
> = {
  ALL: { status: 'ALL', timing: 'ALL', direction: 'desc' },
  UPCOMING: { status: 'OPEN', timing: 'UPCOMING', direction: 'asc' },
  TO_CLOSE: { status: 'OPEN', timing: 'STARTED', direction: 'desc' },
  COMPLETED: { status: 'COMPLETED', timing: 'ALL', direction: 'desc' },
  CANCELLED: { status: 'CANCELLED', timing: 'ALL', direction: 'desc' },
  NO_SHOW: { status: 'NO_SHOW', timing: 'ALL', direction: 'desc' },
};

/// Os atendimentos de um cliente, agendados e realizados (ADR 0030). A agenda é a dona; a página
/// do cliente só mostra esta lista, montada na composição.
export function ClientAppointments({
  clientId,
  timeZone,
  view,
  onView,
  onOpenClosing,
  onOpenAgenda,
}: {
  clientId: string;
  timeZone: string;
  view: ClientPageView;
  onView: ViewSetter<ClientPageView>;
  onOpenClosing: (appointmentId: string) => void;
  /// Abre o dia do atendimento no calendário da agenda.
  onOpenAgenda: (date: CivilDate) => void;
}) {
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const query = queryOf[view.status];
  const search = view.search.trim();
  const filters = {
    clientId,
    status: query.status,
    timing: query.timing,
    sort: 'startsAt' as const,
    direction: query.direction,
    limit: view.limit,
    offset: view.offset,
    ...(search ? { search } : {}),
  };
  const page = useQuery({
    queryKey: ['company', 'scheduling', 'client-appointments', filters],
    queryFn: () => schedulingApi.listAppointments(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const summary = useQuery({
    queryKey: ['company', 'scheduling', 'client-appointments', clientId, 'summary'],
    queryFn: async () => {
      const count = (status: AppointmentStatusFilter, timing: AppointmentTiming) =>
        schedulingApi
          .listAppointments({
            clientId,
            status,
            timing,
            sort: 'startsAt',
            direction: 'desc',
            limit: 1,
            offset: 0,
          })
          .then((result) => result.total);
      const [upcoming, toClose, completed, cancelled, last] = await Promise.all([
        count('OPEN', 'UPCOMING'),
        count('OPEN', 'STARTED'),
        count('COMPLETED', 'ALL'),
        count('CANCELLED', 'ALL'),
        schedulingApi.listAppointments({
          clientId,
          status: 'COMPLETED',
          sort: 'startsAt',
          direction: 'desc',
          limit: 1,
          offset: 0,
        }),
      ]);
      return { upcoming, toClose, completed, cancelled, last: last.appointments[0] ?? null };
    },
    retry: false,
  });
  const total = page.data?.total ?? 0;
  const label = filterOptions.find((item) => item.value === view.status)!.label;
  const now = useNow();

  function actionOf(appointment: Appointment) {
    if (appointment.status === 'COMPLETED') {
      return (
        <IconButton
          label="Ver atendimento"
          icon={<ClipboardCheck />}
          onClick={() => onOpenClosing(appointment.id)}
        />
      );
    }
    if (!isLive(appointment.status)) return null;
    if (Date.parse(appointment.startsAt) <= now) {
      return (
        <IconButton
          label="Abrir atendimento"
          icon={<ClipboardCheck />}
          onClick={() => onOpenClosing(appointment.id)}
        />
      );
    }
    return (
      <IconButton
        label="Ver na agenda"
        icon={<CalendarDays />}
        onClick={() => onOpenAgenda(civilMoment(appointment.startsAt, timeZone).date)}
      />
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
      {summary.data ? (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-content-secondary">
          <div className="flex items-baseline gap-1.5">
            <dt className="order-2">próximos</dt>
            <dd className="text-lg font-semibold tabular-nums text-content">
              {summary.data.upcoming}
            </dd>
          </div>
          <div className="flex items-baseline gap-1.5">
            <dt className="order-2">abertos</dt>
            <dd className="text-lg font-semibold tabular-nums text-content">
              {summary.data.toClose}
            </dd>
          </div>
          <div className="flex items-baseline gap-1.5">
            <dt className="order-2">realizados</dt>
            <dd className="text-lg font-semibold tabular-nums text-content">
              {summary.data.completed}
            </dd>
          </div>
          <div className="flex items-baseline gap-1.5">
            <dt className="order-2">{summary.data.cancelled === 1 ? 'cancelado' : 'cancelados'}</dt>
            <dd className="text-lg font-semibold tabular-nums text-content">
              {summary.data.cancelled}
            </dd>
          </div>
          {summary.data.last ? (
            <div className="flex items-baseline gap-1.5">
              <dt>Último atendimento:</dt>
              <dd className="font-semibold text-content">
                {when(summary.data.last.startsAt, timeZone)}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      <FilterBar
        searchId="client-appointments-search"
        searchLabel="Buscar atendimento"
        searchPlaceholder="Serviço, profissional ou sala"
        search={view.search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={
          view.status === 'ALL'
            ? []
            : [{ key: 'status', label, onRemove: () => onView({ status: 'ALL' }) }]
        }
        onClear={() => onView({ status: 'ALL' })}
        count={`${String(total)} ${total === 1 ? 'atendimento' : 'atendimentos'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'atendimento' : 'atendimentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar atendimentos do cliente por situação"
          options={filterOptions}
          value={view.status}
          onChange={(status) => onView({ status })}
        />
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando atendimentos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={CalendarClock}
          title="Nenhum atendimento nesse filtro"
          description="Os atendimentos do cliente aparecem aqui assim que forem agendados."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Atendimentos do cliente</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  <th
                    aria-sort={query.direction === 'asc' ? 'ascending' : 'descending'}
                    className="px-5 py-3 text-xs font-semibold text-content-muted"
                  >
                    Quando
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold text-content-muted">Serviço</th>
                  <th className="w-40 px-5 py-3 text-xs font-semibold text-content-muted">
                    Situação
                  </th>
                  <th className="w-56">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.appointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="px-5 py-4">
                      <strong className="block text-sm tabular-nums">
                        {when(appointment.startsAt, timeZone)}
                      </strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {professionalLabel(appointment)}
                        {appointment.roomDisplayName ? ` · ${roomLabel(appointment)}` : ''}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm">{appointment.serviceDisplayName}</td>
                    <td className="px-5 py-4">
                      <AppointmentStatusPill
                        status={appointment.status}
                        requiresConfirmation={appointment.requiresConfirmation}
                      />
                    </td>
                    <td className="px-5 py-4 text-right">{actionOf(appointment)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <ul className="divide-y divide-surface-border">
              {page.data?.appointments.map((appointment) => (
                <li key={appointment.id} className="grid min-w-0 gap-2.5 p-4">
                  <div className="min-w-0">
                    <strong className="block text-sm tabular-nums">
                      {when(appointment.startsAt, timeZone)}
                    </strong>
                    <span className="mt-1 block truncate text-xs text-content-muted">
                      {appointment.serviceDisplayName} · {professionalLabel(appointment)}
                    </span>
                  </div>
                  {/* Situação e ação na mesma linha, quebrando quando não cabem: lado a lado com a
                      data, o selo estourava a borda do cartão a 320 px. */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <AppointmentStatusPill
                        status={appointment.status}
                        requiresConfirmation={appointment.requiresConfirmation}
                      />
                    </div>
                    {actionOf(appointment)}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'atendimento' : 'atendimentos')}
          />
        </div>
      )}
    </div>
  );
}

/// "25 de set. de 2026 · 09:30": o horário marcado, sem segundos.
function when(value: string, timeZone: string): string {
  return `${formatDate(value, timeZone)} · ${formatHourMinute(value, timeZone)}`;
}
